import { PhpArray, PhpFloat, normalizeKey } from '../php-compat/ordered-array';

/** PHP serialize(json_decode(JSON.stringify(value), true)), 64-bit PHP with
 * serialize_precision=-1. This is the retained JSON boundary, not a way to
 * recover native PHP types or integer precision already erased by transport.
 */
export function serializePhpJson(value: unknown): string {
    if (value === null) return 'N;';
    if (typeof value === 'boolean') return `b:${value ? 1 : 0};`;
    if (typeof value === 'string') return `s:${utf8Bytes(value).length}:"${value}";`;
    if (typeof value === 'bigint') return `i:${value};`;
    if (value instanceof PhpFloat) {
        if (Number.isNaN(value.value)) return 'd:NAN;';
        if (!Number.isFinite(value.value)) return value.value < 0 ? 'd:-INF;' : 'd:INF;';
        return serializeDouble(value.value);
    }
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) throw new TypeError('Serialization requires finite JSON numbers');
        const encoded = JSON.stringify(value);
        if (/^-?\d+$/.test(encoded)) {
            const integer = BigInt(encoded);
            if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return `i:${encoded};`;
        }
        return serializeDouble(value);
    }
    if (typeof value !== 'object') throw new TypeError('Serialization requires JSON values');
    const entries = value instanceof PhpArray ? [...value] : Object.entries(value);
    const keyText = (key: string | number | bigint): string => {
        const normalized = normalizeKey(key);
        return typeof normalized === 'string' ? serializePhpJson(normalized) : `i:${normalized};`;
    };
    return `a:${entries.length}:{${entries.map(([key,child]) => keyText(key) + serializePhpJson(child)).join('')}}`;
}

function serializeDouble(value: number): string {
    if (Object.is(value, -0)) return 'd:-0;';
    const [mantissa = '', power = '0'] = value.toExponential().split('e');
    const exponent = Number(power);
    const text = exponent < -4 || exponent >= 17
        ? `${mantissa.includes('.') ? mantissa : mantissa + '.0'}E${exponent >= 0 ? '+' : ''}${exponent}` : String(value);
    return `d:${text};`;
}

export function utf8Bytes(value: string): number[] {
    const bytes: number[] = [];
    for (const character of value) {
        const point = character.codePointAt(0)!;
        if (point >= 0xd800 && point <= 0xdfff) throw new TypeError('PHP JSON requires valid Unicode');
        if (point < 0x80) bytes.push(point);
        else if (point < 0x800) bytes.push(0xc0 | point >> 6, 0x80 | point & 63);
        else if (point < 0x10000) bytes.push(0xe0 | point >> 12, 0x80 | point >> 6 & 63, 0x80 | point & 63);
        else bytes.push(0xf0 | point >> 18, 0x80 | point >> 12 & 63, 0x80 | point >> 6 & 63, 0x80 | point & 63);
    }
    return bytes;
}

/** MD5 is retained solely for PHP's anonymous-row display identity, never for
 * security. No browser/node crypto dependency or mutable process cache.
 */
export function md5PhpJson(value: unknown): string {
    return md5PhpString(serializePhpJson(value));
}

/** PHP md5(string): shared with unwired compile signatures/synthetic ids. */
export function md5PhpString(value: string): string {
    const bytes = utf8Bytes(value), bitLength = BigInt(bytes.length) * 8n;
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (let i = 0; i < 8; i++) bytes.push(Number(bitLength >> BigInt(i * 8) & 255n));
    const shifts = [7,12,17,22, 5,9,14,20, 4,11,16,23, 6,10,15,21];
    let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
    for (let offset = 0; offset < bytes.length; offset += 64) {
        const words = Array.from({length:16}, (_,i) => {
            const at = offset + i * 4;
            return bytes[at]! | bytes[at + 1]! << 8 | bytes[at + 2]! << 16 | bytes[at + 3]! << 24;
        });
        let a = a0, b = b0, c = c0, d = d0;
        for (let i = 0; i < 64; i++) {
            const round = Math.floor(i / 16);
            const f = round === 0 ? (b & c) | (~b & d) : round === 1 ? (d & b) | (~d & c) : round === 2 ? b ^ c ^ d : c ^ (b | ~d);
            const g = round === 0 ? i : round === 1 ? (5 * i + 1) % 16 : round === 2 ? (3 * i + 5) % 16 : (7 * i) % 16;
            const sum = (a + f + Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296) + words[g]!) | 0;
            const shift = shifts[round * 4 + i % 4]!;
            [a,b,c,d] = [d, (b + ((sum << shift) | (sum >>> (32 - shift)))) | 0, b, c];
        }
        a0 = (a0 + a) | 0; b0 = (b0 + b) | 0; c0 = (c0 + c) | 0; d0 = (d0 + d) | 0;
    }
    return [a0,b0,c0,d0].flatMap(word => [0,8,16,24].map(shift => ((word >>> shift) & 255).toString(16).padStart(2,'0'))).join('');
}
